import { QueryKeys } from '@/enums';
import { clearSlabState } from '@/helpers/bundle-builder-storage';
import { syncGuestCart } from '@/helpers/guest-cart-helper';
import { IAddToCartItem, IGetCartProduct } from '@/interfaces/cart';
import { IProduct } from '@/interfaces/product';
import { getBundlePromotionBanner, getBundlePromotionSlabs } from '@/services/bundlePromotion.service';
import { addToCart } from '@/services/cart.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useCart } from '@/hooks/cart';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { IBundlePromotionBanner, IBundleSlab } from '@/interfaces/bundlePromotion';
import useHeaderStore from '@/store/header';

export const bundleSlabsQueryOptions = (slug?: string) => ({
  queryKey: [QueryKeys.productBundlePromotions, slug ?? 'all'],
  queryFn: () => getBundlePromotionSlabs(slug),
});

export const useBundlePromotionSlabs = () =>
  useQuery<IBundleSlab[]>({
    ...bundleSlabsQueryOptions(),
    refetchOnWindowFocus: false,
    retry: false,
  });

export const useBundlePromotionBanner = () =>
  useQuery<IBundlePromotionBanner>({
    queryKey: [QueryKeys.bundlePromotionBanner],
    queryFn: () => getBundlePromotionBanner(),
    refetchOnWindowFocus: false,
    retry: false,
  });

export const useAddBundleToCart = () => {
  const auth = useReactQuerySession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IAddToCartItem): Promise<IProduct | IGetCartProduct> =>
      auth.isAuthenticated ? addToCart(payload, auth?.session) : syncGuestCart(undefined, payload.addtoCartBundleInfo),
    onSuccess: (_data, variables) => {
      const slabId = variables.addtoCartBundleInfo?.slabId;
      if (slabId) clearSlabState(slabId);
      queryClient.invalidateQueries({ queryKey: [QueryKeys.cartItems] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.orderSummary] });
      openNotification('success', 'Bundle', 'Bundle added to cart successfully.');
    },
    onError: (error) => {
      const apiError = error as unknown as { data?: { Messages?: string[]; Exception?: string } };
      const message = apiError?.data?.Messages?.[0] || apiError?.data?.Exception || 'Failed to add bundle to cart. Please try again.';
      openNotification('danger', 'Bundle', message);
    },
  });
};

const LAST_KNOWN_BUNDLE_IDS_KEY = 'bundle-unlock-toast:last-known-bundle-ids';

const readLastKnownBundleIds = (): Set<string> | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(LAST_KNOWN_BUNDLE_IDS_KEY);
    if (raw === null) return null; // nothing recorded yet in this tab — true cold start
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return null;
  }
};

const writeLastKnownBundleIds = (ids: Set<string>): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(LAST_KNOWN_BUNDLE_IDS_KEY, JSON.stringify([...ids]));
};

export const useBundleUnlockToast = () => {
  const queryClient = useQueryClient();
  const auth = useReactQuerySession();
  const pathname = usePathname();
  const isProductDetailsPage = pathname?.startsWith('/product/') ?? false;
  const isBundlePromotionPage = pathname?.startsWith('/bundle') ?? false;
  const selectedStore = useHeaderStore((state) => state.selectedStore);
  const { data: cartData, isPlaceholderData } = useCart(queryClient, auth?.session, [selectedStore?.Code]);
  const previousBundleIdsRef = useRef<Set<string> | null | 'uninitialized'>('uninitialized');

  useEffect(() => {
    if (auth.loading || isPlaceholderData || !cartData?.Items) return;

    if (previousBundleIdsRef.current === 'uninitialized') {
      previousBundleIdsRef.current = readLastKnownBundleIds();
    }

    const currentBundleIds = new Set(cartData.Items.map((item) => item.CartBundleInfoDto?.BundleId).filter((id): id is string => Boolean(id)));

    if (previousBundleIdsRef.current) {
      const previousBundleIds = previousBundleIdsRef.current;
      const added = [...currentBundleIds].filter((id) => !previousBundleIds.has(id));
      const removed = [...previousBundleIds].filter((id) => !currentBundleIds.has(id));

      // An empty cart (order placed, cart cleared) can't have an unmet requirement, so nothing to warn about.
      const isCartEmpty = cartData.Items.length === 0;

      if (!isProductDetailsPage && !isBundlePromotionPage && !isCartEmpty) {
        if (added.length > 0 && removed.length > 0) {
          openNotification(
            'success',
            'Bundle Updated',
            'Your cart no longer qualifies for the previous bundle offer, but a different bundle discount now applies.',
          );
        } else if (added.length > 0) {
          openNotification('success', 'Bundle Unlocked', 'A bundle discount has been applied to your cart.');
        } else if (removed.length > 0) {
          openNotification('danger', 'Bundle Removed', "Your bundle discount was removed because the offer's requirement is no longer met.");
        }
      }
    }

    previousBundleIdsRef.current = currentBundleIds;
    writeLastKnownBundleIds(currentBundleIds);
  }, [cartData, auth.loading, isPlaceholderData]);
};
